import React, { useState } from 'react';

import { withRouter } from 'react-router';
import { connect } from 'react-redux';

import { Auth } from 'aws-amplify';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { withStyles } from '@material-ui/styles';

import BobModal from '../../../components/BobModal';
import BobConfirmButton from '../../../components/BobConfirmButton';
import BobCancelButton from '../../../components/BobCancelButton';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Tooltip from '@material-ui/core/Tooltip';

import { awsErrorMessages } from '../../../helpers/awsErrorMessages';

import * as AuthActions from '../../../actions/authActions';
import * as ApplicationMessageActions from '../../../actions/applicationMessageActions';

import * as classes from './AddNewEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    oldPassword: Yup.string()
        .required(),
    newPassword: Yup.string()
        .min(8)
        .max(99)
        .matches(
            // eslint-disable-next-line no-useless-escape
            /^(?=[0-9a-zA-Z\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]{6,}$)(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=[^0-9]*[0-9])(?=[^\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]*[\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]).*/,
            'Password does not match required format.'
        )
        .required('Password required'),
    repeatNewPassword: Yup.string().when('newPassword', {
        is:   val => (val && val.length > 0 ? true : false),
        then: Yup.string().oneOf(
            [Yup.ref('newPassword')],
            'New passwords must match.'
        )
    })
});

const HtmlTooltip = withStyles((theme) => ({
    tooltip: {
        backgroundColor: 'white',
        color:           'black',
        fontSize:        theme.typography.pxToRem(12),
        border:          '1px solid grey',
        borderRadius:    '5px'
    }
}))(Tooltip);

const UpdatePasswordModal = ({
    enqueueSnackbar,
    handleUpdatePassword,
    history,
    signOut,
    ...props
}) => {

    const [isLoading, setIsLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            oldPassword:       '',
            newPassword:       '',
            repeatNewPassword: ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            setIsLoading(true);
            Auth.currentAuthenticatedUser()
                .then(user => {
                    return Auth.changePassword(user, values.oldPassword, values.newPassword);
                })
                .then(data => {
                    enqueueSnackbar('Password changed successfully.', 'success');
                    setIsLoading(false);
                    handleUpdatePassword();
                    setTimeout(() => {
                        history.push('/');
                        signOut();
                    }, 3000);
                })
                .catch(err => {
                    let errorMessage;
                    if (err.message) {
                        if (awsErrorMessages[err.message]) {
                            errorMessage = awsErrorMessages[err.message];
                        } else {
                            errorMessage = err.message;
                        }
                    } else {
                        if (awsErrorMessages[err]) {
                            errorMessage = awsErrorMessages[err];
                        } else {
                            errorMessage = err;
                        }
                    }

                    enqueueSnackbar(errorMessage, 'error');
                    setIsLoading(false);
                    setSubmitting(false);
                });
        }
    });

    return (
        <BobModal
            title={'Change Password'}
            hidebuttons={true}
            headerOverride={classes.HeaderOverride}
            isRemoveFooter={true}
            onClose={() => handleUpdatePassword()}
        >
            <div className={classes.ModalBody}>
                <div>
                    <FormControl className={classes.Row}>
                        <TextField
                            className={classes.Input}
                            id="oldPassword"
                            name="oldPassword"
                            type="password"
                            value={formik.values.oldPassword}
                            label={<span>Password<span className={classes.required}> *</span></span>}
                            placeholder="Enter"
                            size="small"
                            onChange={formik.handleChange}
                            InputProps={{
                                onBlur: formik.handleBlur
                            }}
                            variant="outlined"
                            FormHelperTextProps={{
                                classes: {
                                    error: classes.error
                                }
                            }}
                            error={Boolean(formik.errors.oldPassword) && Boolean(formik.touched.oldPassword)}
                            helperText={Boolean(formik.touched.oldPassword) && formik.errors.oldPassword}
                        />
                    </FormControl>
                </div>
                <div>
                    <FormControl className={classes.Row}>
                        <TextField
                            className={classes.Input}
                            id="newPassword"
                            name="newPassword"
                            type="password"
                            value={formik.values.newPassword}
                            label={<span>New Password<span className={classes.required}> *</span></span>}
                            placeholder="Enter"
                            size="small"
                            onChange={formik.handleChange}
                            InputProps={{
                                onBlur:       formik.handleBlur,
                                endAdornment: <InputAdornment position="end">
                                    <HtmlTooltip
                                        title={
                                            <span className={classes.Tooltip}>
                                                <span className={classes.ToolTipHeader}>Password Requirements:</span>
                                                <ul>
                                                    <li className={classes.ToolTipList}>At Least 8 Characters Long</li>
                                                    <li className={classes.ToolTipList}>At Least 1 Number</li>
                                                    <li className={classes.ToolTipList}>At Least 1 Special Character</li>
                                                    <li className={classes.ToolTipList}>At Least 1 Uppercase Character</li>
                                                    <li className={classes.ToolTipList}>At Least 1 Lowercase Character</li>
                                                    <li className={classes.ToolTipList}>No Spaces</li>
                                                </ul>
                                            </span>
                                        }
                                        placement="top-start"
                                    >
                                        <span className={clsx('material-icons-outlined md-18', classes.HelperIcon)}>help_outline</span>
                                    </HtmlTooltip>
                                </InputAdornment>
                            }}  
                            variant="outlined"
                            FormHelperTextProps={{
                                classes: {
                                    error: classes.error
                                }
                            }}
                            error={Boolean(formik.errors.newPassword) && Boolean(formik.touched.newPassword)}
                            helperText={Boolean(formik.touched.newPassword) && formik.errors.newPassword}
                        />
                    </FormControl>
                </div>
                <div>
                    <FormControl className={classes.Row}>
                        <TextField
                            className={classes.Input}
                            id="repeatNewPassword"
                            name="repeatNewPassword"
                            type="password"
                            value={formik.values.repeatNewPassword}
                            label={<span>Confirm New Password<span className={classes.required}> *</span></span>}
                            placeholder="Enter"
                            size="small"
                            onChange={formik.handleChange}
                            InputProps={{
                                onBlur: formik.handleBlur
                            }}
                            variant="outlined"
                            FormHelperTextProps={{
                                classes: {
                                    error: classes.error
                                }
                            }}
                            error={Boolean(formik.errors.repeatNewPassword) && Boolean(formik.touched.repeatNewPassword)}
                            helperText={Boolean(formik.touched.repeatNewPassword) && formik.errors.repeatNewPassword}
                        />
                    </FormControl>
                </div>
                <div className={classes.ButtonContainer}>
                    <BobCancelButton
                        className={classes.CancelButton}
                        disabled={isLoading}
                        onClick={() => handleUpdatePassword()}
                        label={'Cancel'}
                    />
                    <BobConfirmButton
                        className={classes.Buttons}
                        onClick={formik.handleSubmit}
                        label={'Update'}
                        iconSize={15}
                        isLoading={isLoading}
                    />
                </div>
            </div>
        </BobModal>
    );
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...AuthActions
};

export default withRouter(connect(null, mapDispatchToProps)(UpdatePasswordModal));
